import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { trackFeatureUsage, trackSearch, trackEvent } from '../utils/analytics';
import { formatDateForDisplay } from '../utils/dateFormat';

const AffiliatedCustomers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [affiliatedUsers, setAffiliatedUsers] = useState([]);
  const [selectedAffiliatedUser, setSelectedAffiliatedUser] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState('fullName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [pageSize] = useState(15);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  useEffect(() => {
    fetchAffiliatedUsers();
    trackFeatureUsage('Affiliated Customers Management', 'view');
  }, []);

  useEffect(() => {
    fetchAffiliatedCustomers();
  }, [currentPage, sortField, sortDirection, searchQuery, selectedAffiliatedUser]);

  const fetchAffiliatedUsers = async () => {
    try {
      const response = await axios.get('/api/customers/affiliated-users');
      setAffiliatedUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch affiliated users:', error);
      setError('Failed to load affiliated users. Please try again.');
    }
  };

  const fetchAffiliatedCustomers = async () => {
    try {
      setSearchLoading(true);
      
      const params = {
        page: currentPage,
        size: pageSize,
        search: searchQuery,
        sortBy: sortField,
        sortDir: sortDirection
      };
      
      // Add affiliated user filter if not ALL
      if (selectedAffiliatedUser !== 'ALL') {
        params.affiliatedUserId = selectedAffiliatedUser;
      }
      
      const response = await axios.get('/api/customers/affiliated-customers-paged', { params });
      const data = response.data;
      
      setCustomers(data.content);
      setTotalPages(data.totalPages);
      setTotalElements(data.totalElements);
    } catch (error) {
      console.error('Failed to fetch affiliated customers:', error);
      setError('Failed to load affiliated customers. Please try again.');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(0);
  };

  const handleSearch = (query) => {
    if (query && query.length >= 2) {
      trackSearch(query, 'affiliated_customers');
    }
    setSearchQuery(query);
    setCurrentPage(0);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'ACTIVE': return 'var(--success)';
      case 'INACTIVE': return 'var(--warning)';
      case 'BLOCKED': return 'var(--error)';
      default: return 'var(--gray-dark)';
    }
  };

  const handleViewCustomer = (customer) => {
    trackEvent('view_affiliated_customer_details', {
      event_category: 'Affiliated Customer Management',
      event_label: customer.fullName,
      customer_id: customer.id
    });
    navigate(`/affiliated-customers/${customer.id}`);
  };

  const handleAffiliatedUserChange = (userId) => {
    setSelectedAffiliatedUser(userId);
    setCurrentPage(0);
    
    trackEvent('affiliated_user_filter_changed', {
      event_category: 'Affiliated Customer Management',
      event_label: userId === 'ALL' ? 'All Users' : affiliatedUsers.find(u => u.id.toString() === userId)?.fullName || 'Unknown'
    });
  };

  if (loading && affiliatedUsers.length === 0) {
    return (
      <div className="page">
        <div className="loading">Loading affiliated customers...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        marginBottom: '30px',
        flexWrap: 'wrap',
        gap: '15px'
      }}>
        <div style={{ flex: '1', minWidth: '200px' }}>
          <button 
            onClick={() => navigate('/dashboard')} 
            className="btn btn-secondary"
            style={{ marginBottom: '10px' }}
          >
            ← Back to Dashboard
          </button>
          <h1 className="page-title" style={{ margin: 0 }}>Affiliated Customers</h1>
          <div style={{ 
            fontSize: '14px', 
            color: 'var(--gray-dark)', 
            marginTop: '5px'
          }}>
            Customers from your affiliated club owners
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* Affiliated User Filter */}
      {affiliatedUsers.length > 1 && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <label htmlFor="affiliatedUserFilter" className="form-label" style={{ margin: 0, minWidth: '120px' }}>
              Filter by Club Owner:
            </label>
            <select
              id="affiliatedUserFilter"
              className="form-input"
              value={selectedAffiliatedUser}
              onChange={(e) => handleAffiliatedUserChange(e.target.value)}
              style={{ margin: 0, maxWidth: '300px' }}
            >
              <option value="ALL">All Affiliated Customers</option>
              {affiliatedUsers.map(affiliatedUser => (
                <option key={affiliatedUser.id} value={affiliatedUser.id}>
                  {affiliatedUser.fullName} ({affiliatedUser.email})
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: 'var(--dark-blue)', margin: 0 }}>
            {selectedAffiliatedUser === 'ALL' 
              ? `All Affiliated Customers (${totalElements} total)`
              : `${affiliatedUsers.find(u => u.id.toString() === selectedAffiliatedUser)?.fullName || 'Selected'} Customers (${totalElements} total)`
            }
          </h3>
          <div style={{ position: 'relative', width: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search affiliated customers..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ margin: 0, paddingRight: searchLoading ? '40px' : '35px' }}
            />
            {searchLoading && (
              <div style={{
                position: 'absolute',
                right: '35px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--primary-blue)'
              }}>
                ⏳
              </div>
            )}
            {searchQuery && (
              <button
                onClick={() => handleSearch('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--gray-dark)'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Pagination Info */}
        {totalElements > 0 && (
          <div style={{ 
            marginBottom: '15px', 
            fontSize: '14px', 
            color: 'var(--gray-dark)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>
              Showing {currentPage * pageSize + 1} to {Math.min((currentPage + 1) * pageSize, totalElements)} of {totalElements} customers
              {searchQuery && ` (filtered by "${searchQuery}")`}
            </span>
            <span>
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
        )}

        {customers.length === 0 ? (
          searchQuery ? (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No affiliated customers found matching "{searchQuery}".
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: 'var(--gray-dark)' }}>
              No affiliated customers found. Affiliated customers will appear here when other club owners are affiliated with you.
            </div>
          )
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--pale-blue)' }}>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('fullName')}
                  >
                    Customer Name {getSortIcon('fullName')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('phone')}
                  >
                    Phone {getSortIcon('phone')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('customerType')}
                  >
                    Type {getSortIcon('customerType')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('totalWeightLoss')}
                  >
                    Weight Loss {getSortIcon('totalWeightLoss')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('referralCount')}
                  >
                    Referrals {getSortIcon('referralCount')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('totalAmountDue')}
                  >
                    Amount Due {getSortIcon('totalAmountDue')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('programEndDate')}
                  >
                    Program End {getSortIcon('programEndDate')}
                  </th>
                  <th 
                    style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      cursor: 'pointer',
                      borderBottom: '2px solid var(--light-blue)'
                    }}
                    onClick={() => handleSort('status')}
                  >
                    Status {getSortIcon('status')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {customers.map((customer, index) => (
                  <tr 
                    key={customer.id}
                    onClick={() => handleViewCustomer(customer)}
                    style={{ 
                      backgroundColor: index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)',
                      borderBottom: '1px solid var(--gray-medium)',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => e.target.parentElement.style.backgroundColor = 'var(--pale-blue)'}
                    onMouseLeave={(e) => e.target.parentElement.style.backgroundColor = index % 2 === 0 ? 'var(--white)' : 'var(--gray-light)'}
                  >
                    <td style={{ padding: '12px', fontWeight: '500' }}>
                      {customer.fullName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.phone || 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: customer.customerType === 'ONLINE' ? 'var(--primary-blue)' : 'var(--success)',
                        color: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px'
                      }}>
                        {customer.customerType || 'N/A'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.totalWeightLoss !== null && customer.totalWeightLoss !== undefined ? (
                        <span style={{
                          color: customer.totalWeightLoss >= 0 ? 'var(--success)' : 'var(--error)',
                          fontWeight: 'bold'
                        }}>
                          {customer.totalWeightLoss >= 0 ? '-' : '+'}{Math.abs(customer.totalWeightLoss)} kg
                        </span>
                      ) : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        background: customer.referralCount > 0 ? 'var(--success)' : 'var(--gray-medium)',
                        color: customer.referralCount > 0 ? 'white' : 'var(--gray-dark)',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold'
                      }}>
                        {customer.referralCount || 0}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.totalAmountDue > 0 ? (
                        <span style={{
                          color: 'var(--error)',
                          fontWeight: 'bold'
                        }}>
                          ₹{customer.totalAmountDue}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--success)' }}>₹0</span>
                      )}
                    </td>
                    <td style={{ padding: '12px' }}>
                      {customer.programEndDate ? formatDateForDisplay(customer.programEndDate) : 'N/A'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        color: getStatusColor(customer.status),
                        fontWeight: '500'
                      }}>
                        {customer.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px'
          }}>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(0)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              First
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Previous
            </button>
            
            {/* Page Numbers */}
            <div style={{ display: 'flex', gap: '5px' }}>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 3) {
                  pageNum = i;
                } else if (currentPage > totalPages - 4) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    className={`btn ${currentPage === pageNum ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => handlePageChange(pageNum)}
                    disabled={loading}
                    style={{ 
                      fontSize: '12px', 
                      padding: '6px 10px',
                      minWidth: '35px'
                    }}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>
            
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Next
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={currentPage === totalPages - 1 || loading}
              style={{ fontSize: '12px', padding: '6px 12px' }}
            >
              Last
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliatedCustomers;
